/**
 * PRIVACY: Geen user-input naar server, localStorage (behalve onboarding + UI-voorkeuren),
 * externe APIs. PortalAuth verify via SDK; minimale Umami analytics (tool_open/close).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  ToolLayout,
  PaywallModal,
  Onboarding,
  useAccess,
  hasCompletedOnboarding,
  setOnboardingComplete,
  type OnboardingSlide,
} from "@jari-tools/shared";
import { RemoteApp } from "./RemoteApp";
import bodyHtml from "./emdr-body.html?raw";
import "./tool.css";
import "./design-colors.css";
import "./style.css";
import "./figures.css";

const TOOL_SLUG = import.meta.env.VITE_TOOL_SLUG || "emdr";
const TOOL_NAME = "EMDR — Bilaterale lichtbol";
const PORTAL_URL = import.meta.env.VITE_PORTAL_URL;

const ONBOARDING_SLIDES: OnboardingSlide[] = [
  {
    title: "Bilaterale stimulatie",
    body: "Visuele BLS ter ondersteuning van sessies. Geen gecertificeerd EMDR-protocol.",
  },
  {
    title: "Licentie",
    body: "Bewegende figuren, presets (ook ingebouwd) en .jari export/import vereisen een licentie via het portal.",
  },
  {
    title: "Experimenteel",
    body: "Trauma-woorden en effecten blijven lokaal in de browser — niets wordt verzonden.",
  },
];

export function App() {
  const remoteParam = new URLSearchParams(location.search).get("remote");
  if (remoteParam !== null) {
    return <RemoteApp initialCode={remoteParam || null} />;
  }
  return <DesktopApp />;
}

function DesktopApp() {
  const { access, name, loading, error, isPaid } = useAccess();
  const hostRef = useRef<HTMLDivElement>(null);
  const bootedRef = useRef(false);
  const [showOnboarding, setShowOnboarding] = useState(
    () => !hasCompletedOnboarding(TOOL_SLUG)
  );
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallFeature, setPaywallFeature] = useState("deze functie");

  const completeOnboarding = useCallback(() => {
    setOnboardingComplete(TOOL_SLUG);
    setShowOnboarding(false);
  }, []);

  const onPaywall = useCallback((feature: string) => {
    setPaywallFeature(feature);
    setPaywallOpen(true);
  }, []);

  useEffect(() => {
    if (!hostRef.current || bootedRef.current) return;
    hostRef.current.innerHTML = bodyHtml;
    bootedRef.current = true;
    import("./engine/bootstrap").then(({ bootstrapEmdr }) => {
      bootstrapEmdr({ isPaid, onPaywall });
    });
  }, [onPaywall]);

  useEffect(() => {
    window.__EMDR_IS_PAID__ = isPaid;
    window.__EMDR_REFRESH_PAID_UI__?.();
  }, [isPaid]);

  return (
    <>
      <ToolLayout
        toolName={TOOL_NAME}
        access={access}
        userName={name}
        loading={loading}
        error={error}
        portalUrl={PORTAL_URL}
        onShowOnboarding={() => setShowOnboarding(true)}
      >
        <div className="emdr-host jari-tool-emdr" ref={hostRef} />
      </ToolLayout>

      <Onboarding
        open={showOnboarding}
        slides={ONBOARDING_SLIDES}
        onComplete={completeOnboarding}
        onSkip={completeOnboarding}
      />

      <PaywallModal
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        featureTitle={paywallFeature}
        portalUrl={PORTAL_URL}
      />
    </>
  );
}
